import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip as RTooltip, Legend,
} from "recharts";
import {
  Building2, ArrowLeft, TrendingUp, CalendarDays, Receipt, Wallet, Info, Landmark,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

// =============================================================================
// Commercial → Premises (mig 385, reworked by mig 386)
// =============================================================================
// James, 22 Sep 2026: "a new cost screen for the premises based in commercials
// and drives OPEX forecasting for the next year and current spend … with year
// to date, monthly spend, and some nice clean widget/visuals".
//
// James, 23 Sep 2026, on the five premises budget codes 385 added: "1 would
// have been fine all to PREM and the XERO COA is what should be driving the
// premises cost breakdown, we have hundreds of Xero accounts."
//
// So: every premises bill is coded OX03_PREMISES, and the breakdown on this
// screen is by the XERO ACCOUNT on the bill — 469 Rent, 470 Rates & Body
// Corporate, 445 Light Power Water — read from v_premises_accounts, which is
// the bookkeeper's list in finance_settings plus anything a bill actually hit.
// The 799 bond is a balance-sheet account: it is listed in its own section so
// the money is visible, and never counted in year-to-date, monthly or the
// forecast, because a bond is not a running cost and would inflate next year.
//
// Everything here is READ-ONLY. Ops never pays anything — bills are captured
// and coded in AP, paid in Xero, and this screen reports what they came to.
//
// Forecast for the next 12 months, per cost line:
//   · the standing schedule, when a fixed OPEX line is linked to that account
//     (OPEX tracker → premises account); otherwise
//   · the last 12 months of real bills, projected forward.
// Each line says which of the two it used.
// =============================================================================

// Colours keyed by Xero account. Anything else falls through the palette.
const COLOURS: Record<string, string> = {
  "469":   "#F97316",   // rent
  "470":   "#10B981",   // rates & body corporate (incl. the PM fee)
  "445":   "#EAB308",   // light, power, water
  "799":   "#6366F1",   // bond
  uncoded: "#6B7280",
};
const FALLBACK_COLOURS = ["#EC4899", "#14B8A6", "#A855F7", "#84CC16", "#EF4444", "#3B82F6"];

// Monthly multiplier for a fixed OPEX line's billing frequency. Same numbers
// the OPEX tracker uses — a quarterly bill is a third of a month, a one-off is
// not a standing cost at all.
const MONTHLY_MULT: Record<string, number> = {
  weekly: 52 / 12, fortnightly: 26 / 12, monthly: 1,
  quarterly: 1 / 3, annually: 1 / 12, one_off: 0,
};

// Local date keys only — never toISOString().slice(0,10), which rolls the
// boundary back a day in Brisbane before 10am.
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
// Australian financial year: 1 July – 30 June.
function fyStart(now = new Date()): Date {
  const y = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return new Date(y, 6, 1);
}
function fyLabel(now = new Date()): string {
  const s = fyStart(now).getFullYear();
  return `FY${String(s).slice(2)}/${String(s + 1).slice(2)}`;
}
function monthLabel(key: string): string {
  const [y, m] = key.split("-");
  return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(m) - 1]} ${y.slice(2)}`;
}

interface CostLine {
  xero_account_code: string;
  xero_account_name: string;
  account_type: string | null;
  in_chart: boolean;
  in_map: boolean;
  is_running_cost: boolean;
  sort_order: number;
}

const UNCODED: CostLine = {
  xero_account_code: "uncoded", xero_account_name: "No Xero account on the bill",
  account_type: null, in_chart: false, in_map: false, is_running_cost: true, sort_order: 9999,
};

export default function PremisesScreen() {
  const user = useRequireAuth();
  const [lineFilter, setLineFilter] = useState<string>("all");

  const today   = new Date();
  const fyFrom  = dayKey(fyStart(today));
  const thisMth = monthKey(today);
  // 24 months of history: 12 to report on, 12 more so a yearly bill that fell
  // just outside the window doesn't make a cost line look free.
  const from24  = dayKey(new Date(today.getFullYear(), today.getMonth() - 23, 1));
  const from12  = monthKey(new Date(today.getFullYear(), today.getMonth() - 11, 1));

  // The cost lines — the bookkeeper's premises accounts (finance_settings) plus
  // any account a premises bill actually carries. Listed even at zero, so a
  // missing power bill is visible as a zero rather than absent.
  const { data: lines = [] } = useQuery({
    queryKey: ["premises_accounts"],
    enabled: hasRole(user, "director"),
    queryFn: async () => {
      // v_premises_accounts is new in 386 — database.types.ts hasn't been regened.
      const { data, error } = await (supabase as any)
        .from("v_premises_accounts")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as CostLine[];
    },
  });

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["premises_spend", from24],
    enabled: hasRole(user, "director"),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_premises_spend")
        .select("*")
        .gte("spend_date", from24)
        .order("spend_date", { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Fixed OPEX lines a director has pointed at a premises account (386).
  const { data: schedule = [] } = useQuery({
    queryKey: ["premises_schedule"],
    enabled: hasRole(user, "director"),
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("opex_items")
        .select("opex_id, item_name, amount_ex_gst, frequency, premises_xero_account, end_date")
        .eq("is_active", true)
        .not("premises_xero_account", "is", null);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  // Every line the screen can show: the chart's list, plus "uncoded" when a
  // bill has no account at all (it still cost money).
  const allLines = useMemo<CostLine[]>(() => {
    const out = [...lines];
    if (rows.some(r => r.cost_line === "uncoded") && !out.some(l => l.xero_account_code === "uncoded")) out.push(UNCODED);
    return out;
  }, [lines, rows]);
  const runningLines = useMemo(() => allLines.filter(l => l.is_running_cost), [allLines]);
  const balanceLines = useMemo(() => allLines.filter(l => !l.is_running_cost), [allLines]);
  const isRunning = useMemo(() => {
    const m = new Map(allLines.map(l => [l.xero_account_code, l.is_running_cost]));
    return (code: string) => m.get(code) ?? true;
  }, [allLines]);

  const colourFor = (code: string, i: number) =>
    COLOURS[code] ?? FALLBACK_COLOURS[i % FALLBACK_COLOURS.length];

  const nameFor = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of allLines) m.set(l.xero_account_code, l.xero_account_name);
    return (code: string) => m.get(code) ?? (code === "uncoded" ? UNCODED.xero_account_name : `Xero ${code}`);
  }, [allLines]);

  // Running-cost bills only, for every total on the screen. The bond and any
  // other balance-sheet line are reported separately below.
  const runningRows = useMemo(() => rows.filter(r => isRunning(String(r.cost_line))), [rows, isRunning]);
  const balanceRows = useMemo(() => rows.filter(r => !isRunning(String(r.cost_line))), [rows, isRunning]);

  // -- headline numbers ------------------------------------------------------
  const totals = useMemo(() => {
    let fy = 0, month = 0, last12 = 0;
    for (const r of runningRows) {
      const v = Number(r.amount_ex_gst ?? 0);
      if (r.spend_date >= fyFrom) fy += v;
      if (r.spend_month === thisMth) month += v;
      if (r.spend_month >= from12) last12 += v;
    }
    return { fy, month, last12, avgMonth: last12 / 12 };
  }, [runningRows, fyFrom, thisMth, from12]);

  // -- this financial year, split by cost line -------------------------------
  const byLine = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of runningLines) m.set(l.xero_account_code, 0);
    for (const r of runningRows) {
      if (r.spend_date < fyFrom) continue;
      const code = String(r.cost_line);
      m.set(code, (m.get(code) ?? 0) + Number(r.amount_ex_gst ?? 0));
    }
    return Array.from(m.entries())
      .map(([code, value]) => ({ code, name: nameFor(code), value }))
      .sort((a, b) => b.value - a.value);
  }, [runningRows, runningLines, fyFrom, nameFor]);
  const byLineWithSpend = byLine.filter(c => c.value > 0);

  // -- month by month, stacked by cost line ----------------------------------
  const monthly = useMemo(() => {
    const months: string[] = [];
    for (let i = 11; i >= 0; i--) {
      months.push(monthKey(new Date(today.getFullYear(), today.getMonth() - i, 1)));
    }
    const base = months.map(k => {
      const row: any = { month: k, label: monthLabel(k), total: 0 };
      for (const l of runningLines) row[l.xero_account_code] = 0;
      return row;
    });
    const index = new Map(base.map(r => [r.month, r]));
    for (const r of runningRows) {
      const hit = index.get(r.spend_month);
      if (!hit) continue;
      const code = String(r.cost_line);
      const v = Number(r.amount_ex_gst ?? 0);
      hit[code] = (hit[code] ?? 0) + v;
      hit.total += v;
    }
    return base;
  }, [runningRows, runningLines, today]);

  // -- next 12 months --------------------------------------------------------
  // Schedule where one is set, trailing actuals where it isn't. Each line says
  // which, because a forecast built on last year's bills is a different kind of
  // number from one built on a lease.
  const forecast = useMemo(() => {
    const trailing = new Map<string, number>();
    for (const r of runningRows) {
      if (r.spend_month < from12) continue;
      const code = String(r.cost_line);
      trailing.set(code, (trailing.get(code) ?? 0) + Number(r.amount_ex_gst ?? 0));
    }
    const sched = new Map<string, { annual: number; items: string[] }>();
    for (const s of schedule as any[]) {
      const code = s.premises_xero_account ? String(s.premises_xero_account) : null;
      if (!code || !isRunning(code)) continue;              // a bond schedule is not a running cost
      const mult = MONTHLY_MULT[String(s.frequency)] ?? 0;
      if (mult === 0) continue;                             // a one-off is not a standing cost
      const annual = Number(s.amount_ex_gst ?? 0) * mult * 12;
      const hit = sched.get(code) ?? { annual: 0, items: [] };
      hit.annual += annual;
      hit.items.push(s.item_name);
      sched.set(code, hit);
    }

    const out = runningLines.map(l => {
      const code = l.xero_account_code;
      const actual = trailing.get(code) ?? 0;
      const s = sched.get(code);
      const basis: "schedule" | "actuals" | "none" =
        s ? "schedule" : actual > 0 ? "actuals" : "none";
      return {
        code,
        name: l.xero_account_name,
        inChart: l.in_chart,
        last12: actual,
        forecast: s ? s.annual : actual,
        basis,
        from: s ? s.items.join(", ") : null,
      };
    });
    // Anything billed to an account not in the list still has to appear or
    // the totals won't add up.
    for (const [code, actual] of trailing) {
      if (out.some(l => l.code === code)) continue;
      out.push({ code, name: nameFor(code), inChart: false, last12: actual, forecast: actual, basis: "actuals", from: null });
    }
    const total = out.reduce((a, l) => a + l.forecast, 0);
    return { lines: out.sort((a, b) => b.forecast - a.forecast), total };
  }, [runningRows, runningLines, schedule, from12, nameFor, isRunning]);

  // -- the bills themselves --------------------------------------------------
  const bills = useMemo(
    () => (lineFilter === "all" ? rows : rows.filter(r => String(r.cost_line) === lineFilter)).slice(0, 200),
    [rows, lineFilter],
  );

  const notInChart = allLines.filter(l => !l.in_chart && l.xero_account_code !== "uncoded");

  if (!hasRole(user, "director")) {
    return (
      <div className="p-6 max-w-md mx-auto">
        <Card><EmptyState title="Directors only" description="Premises costs are restricted to directors." icon={Building2} /></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <Link to="/commercial" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Commercial dashboard
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Building2 size={22} className="text-accent" />
            Premises
          </h1>
          <div className="text-text-muted text-[13px] mt-1">
            What the warehouse costs to run, broken down by the Xero account on each bill.
            Actuals from AP; forecast for the next 12 months.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/opex"><Button variant="secondary" size="sm" icon={Receipt}>OPEX tracker</Button></Link>
          <Link to="/ap"><Button variant="secondary" size="sm" icon={Wallet}>AP</Button></Link>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">
          Premises spend failed to load: {(error as any)?.message ?? String(error)}
        </div>
      )}
      {notInChart.length > 0 && (
        <div className="mb-4 bg-warn/10 border border-warn/30 rounded-md p-3 text-[12px] text-warn">
          {notInChart.map(l => l.xero_account_code).join(", ")} {notInChart.length === 1 ? "is" : "are"} not in Ops's copy of the
          Xero chart of accounts — showing the code instead of a name. Sync the chart from the Xero hand-off screen.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label={`${fyLabel()} to date`} value={fmtMoney(totals.fy)} sub="running costs, ex-GST, from 1 July" icon={CalendarDays} accent />
        <KPI label="This month" value={fmtMoney(totals.month)} sub={monthLabel(thisMth)} />
        <KPI label="Average month" value={fmtMoney(totals.avgMonth)} sub="last 12 months" />
        <KPI label="Next 12 months" value={fmtMoney(forecast.total)} sub="forecast, running costs" icon={TrendingUp} />
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner/></div>
      ) : (
        <>
          {/* This FY by cost line */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
            <Card>
              <div className="p-4">
                <div className="text-[13px] font-medium">{fyLabel()} by Xero account</div>
                <div className="text-[11px] text-text-muted mb-1">{fmtMoney(totals.fy)} ex-GST so far</div>
                {byLineWithSpend.length === 0 ? (
                  <div className="py-10 text-center text-[12px] text-text-muted italic">
                    Nothing billed to premises this financial year yet.
                  </div>
                ) : (
                  <div style={{ width: "100%", height: 190 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={byLineWithSpend} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={1}>
                          {byLineWithSpend.map((c, i) => <Cell key={c.code} fill={colourFor(c.code, i)} />)}
                        </Pie>
                        <RTooltip formatter={(v: any) => fmtMoney(Number(v))} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </Card>

            <Card className="lg:col-span-2">
              <div className="p-4">
                <div className="text-[13px] font-medium mb-2">Where it goes</div>
                <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                  {byLine.map((c, i) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => setLineFilter(lineFilter === c.code ? "all" : c.code)}
                      className={`w-full flex items-center gap-2 text-[12px] rounded px-1 -mx-1 py-1 hover:bg-ink-hover ${
                        lineFilter === c.code ? "bg-ink-hover" : ""
                      }`}
                    >
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: colourFor(c.code, i) }} />
                      <span className="text-text flex-1 truncate text-left">{c.name}</span>
                      <span className="font-mono text-text-dim w-14 text-right">{c.code === "uncoded" ? "—" : c.code}</span>
                      <span className="font-mono text-text-muted w-10 text-right">{totals.fy > 0 ? Math.round((c.value / totals.fy) * 100) : 0}%</span>
                      <span className="font-mono text-text w-24 text-right">{fmtMoney(c.value)}</span>
                    </button>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t border-line text-[11px] text-text-dim flex items-start gap-1.5">
                  <Info size={12} className="mt-0.5 shrink-0"/>
                  <span>
                    Click a line to filter the bills below. The breakdown is the Xero account the bill was coded
                    to in AP — change the account on a bill and it moves. The accounts listed come from
                    Finance settings (premises_xero_accounts); add one there when the bookkeeper starts using it.
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Month by month */}
          <Card className="mb-4">
            <div className="p-4">
              <div className="text-[13px] font-medium">Month by month</div>
              <div className="text-[11px] text-text-muted mb-2">Last 12 months, ex-GST, stacked by Xero account. Running costs only.</div>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={monthly} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff14" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false}
                      tickFormatter={(v: any) => `$${Math.round(Number(v) / 1000)}k`} />
                    <RTooltip
                      formatter={(v: any, n: any) => [fmtMoney(Number(v)), nameFor(String(n))]}
                      contentStyle={{ background: "#111827", border: "1px solid #374151", borderRadius: 6, fontSize: 12 }}
                    />
                    <Legend formatter={(n: any) => nameFor(String(n))} wrapperStyle={{ fontSize: 11 }} />
                    {runningLines.map((l, i) => (
                      <Bar key={l.xero_account_code} dataKey={l.xero_account_code} stackId="premises" fill={colourFor(l.xero_account_code, i)} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>

          {/* Forecast */}
          <Card className="mb-4">
            <div className="p-4 border-b border-line flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="font-semibold text-[14px]">Next 12 months</div>
                <div className="text-[11px] text-text-muted mt-0.5">
                  A cost line uses its standing OPEX schedule when one is linked, and the last 12 months of real bills when it isn't.
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">Forecast</div>
                <div className="font-mono text-[18px] font-semibold text-accent">{fmtMoney(forecast.total)}</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-line">
                  {["Xero account", "Code", "Last 12 months", "Next 12 months", "Per month", "Built from"].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {forecast.lines.map((l, i) => (
                    <tr key={l.code} className={`border-b border-line last:border-b-0 ${i % 2 ? "bg-ink/40" : ""}`}>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colourFor(l.code, i) }} />
                          {l.name}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-[11px] text-text-muted">{l.code === "uncoded" ? "—" : l.code}</td>
                      <td className="px-4 py-2 font-mono text-text-muted">{fmtMoney(l.last12)}</td>
                      <td className="px-4 py-2 font-mono">{fmtMoney(l.forecast)}</td>
                      <td className="px-4 py-2 font-mono text-[12px] text-text-muted">{fmtMoney(l.forecast / 12)}</td>
                      <td className="px-4 py-2 text-[11px]">
                        {l.basis === "schedule"
                          ? <span className="text-ok">Schedule — {l.from}</span>
                          : l.basis === "actuals"
                            ? <span className="text-text-muted">Last 12 months of bills</span>
                            : <span className="text-text-dim italic">No history and no schedule — nothing forecast</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-line text-[11px] text-text-dim">
              Link a fixed cost to a premises account in the OPEX tracker (edit the item → Premises account) and the
              forecast uses that instead of last year's bills. Ops never pays any of this — bills are captured and
              coded here, and paid in Xero.
            </div>
          </Card>

          {/* Balance-sheet items — the bond. Visible, never totalled. */}
          {(balanceLines.length > 0 || balanceRows.length > 0) && (
            <Card className="mb-4">
              <div className="p-4 border-b border-line">
                <div className="font-semibold text-[14px] flex items-center gap-2">
                  <Landmark size={15} className="text-text-muted" /> Held on the premises — not a running cost
                </div>
                <div className="text-[11px] text-text-muted mt-0.5">
                  Balance-sheet accounts (the lease bond). Shown so the money is visible; left out of every total and the forecast above.
                </div>
              </div>
              <div className="divide-y divide-line">
                {balanceLines.map((l, i) => {
                  const sum = balanceRows.filter(r => String(r.cost_line) === l.xero_account_code)
                    .reduce((a, r) => a + Number(r.amount_ex_gst ?? 0), 0);
                  return (
                    <button key={l.xero_account_code} type="button"
                            onClick={() => setLineFilter(lineFilter === l.xero_account_code ? "all" : l.xero_account_code)}
                            className={`w-full px-4 py-2.5 flex items-center gap-3 text-[13px] hover:bg-ink-hover ${lineFilter === l.xero_account_code ? "bg-ink-hover" : ""}`}>
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: colourFor(l.xero_account_code, i) }} />
                      <span className="flex-1 text-left">{l.xero_account_name}</span>
                      <span className="font-mono text-[11px] text-text-muted">{l.xero_account_code}</span>
                      <span className="font-mono w-28 text-right">{fmtMoney(sum)}</span>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}

          {/* The bills */}
          <Card>
            <div className="p-4 border-b border-line flex items-center justify-between gap-2 flex-wrap">
              <div className="font-semibold text-[14px]">
                Premises bills
                {lineFilter !== "all" && <span className="text-text-muted font-normal"> — {nameFor(lineFilter)}</span>}
              </div>
              {lineFilter !== "all" && (
                <Button variant="secondary" size="sm" onClick={() => setLineFilter("all")}>Show all</Button>
              )}
            </div>
            {bills.length === 0 ? (
              <EmptyState
                title="No premises bills"
                description="Bills coded to the premises budget code in AP appear here."
                icon={Receipt}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead><tr className="border-b border-line">
                    {["Date", "Xero account", "Supplier", "Invoice #", "Description", "Ex-GST", "Inc-GST", "Status"].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {bills.map((b: any, i: number) => (
                      <tr key={b.ap_invoice_id} className={`border-b border-line last:border-b-0 ${i % 2 ? "bg-ink/40" : ""}`}>
                        <td className="px-4 py-2 font-mono text-[12px] text-text-muted">{fmtDate(b.spend_date)}</td>
                        <td className="px-4 py-2 text-[12px]">
                          {b.xero_account_name}
                          {b.xero_account_code && <span className="font-mono text-[11px] text-text-dim ml-1.5">{b.xero_account_code}</span>}
                          {!b.is_running_cost && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-ink-hover text-text-muted">balance sheet</span>}
                        </td>
                        <td className="px-4 py-2 text-[12px]">{b.supplier_name ?? "—"}</td>
                        <td className="px-4 py-2 font-mono text-[11px] text-text-muted">{b.invoice_number ?? "—"}</td>
                        <td className="px-4 py-2 text-[12px] max-w-[280px] truncate">{b.description ?? "—"}</td>
                        <td className="px-4 py-2 font-mono">{fmtMoney(b.amount_ex_gst)}</td>
                        <td className="px-4 py-2 font-mono text-text-muted">{fmtMoney(b.amount_inc_gst)}</td>
                        <td className="px-4 py-2">
                          <Pill
                            label={String(b.status).replace(/_/g, " ")}
                            tone={b.status === "paid" ? "approved" : b.status === "disputed" ? "rejected" : "pending"}
                            small
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
